import React, { useEffect, useMemo, useState } from "react";
import { fetchCoursesAPI, fetchUsersAPI, firstError, grantSubscriptionAPI } from "../utils/api";
import { showSuccess } from "../utils/sweetAlert";
import { Modal } from "./ui";

const todayInput = () => {
  const d = new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const formatINR = (value) => `₹${Number(value || 0).toLocaleString("en-IN")}`;

/**
 * Give a student a plan by hand, e.g. after an offline payment.
 * With `user` the student is fixed; without it the admin searches for one.
 */
function GrantSubscriptionModal({ user: fixedUser, onClose, onGranted }) {
  const [courses, setCourses] = useState([]);
  const [loadingCourses, setLoadingCourses] = useState(true);

  const [user, setUser] = useState(fixedUser || null);
  const [userSearch, setUserSearch] = useState("");
  const [userResults, setUserResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [form, setForm] = useState({ planId: "", startsAt: todayInput(), months: "", amount: "", note: "" });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const response = await fetchCoursesAPI();
        if (response?.success) setCourses(response.data || []);
      } finally {
        setLoadingCourses(false);
      }
    })();
  }, []);

  // Student search (only when no student was given)
  useEffect(() => {
    if (fixedUser || user) return undefined;
    const term = userSearch.trim();
    if (term.length < 2) {
      setUserResults([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      setSearching(true);
      try {
        const response = await fetchUsersAPI({ search: term, per_page: 8 });
        setUserResults(response?.success ? response.data || [] : []);
      } finally {
        setSearching(false);
      }
    }, 350);
    return () => clearTimeout(timer);
  }, [userSearch, fixedUser, user]);

  const plans = useMemo(
    () => courses.flatMap((c) => (c.plans || []).map((p) => ({ ...p, course_name: c.course_name }))),
    [courses]
  );
  const plan = plans.find((p) => String(p.id) === String(form.planId));

  const set = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: null }));
  };

  const submit = async () => {
    const next = {};
    if (!user) next.user = "Choose a student.";
    if (!form.planId) next.planId = "Choose a plan.";
    if (form.months && (!Number.isInteger(Number(form.months)) || Number(form.months) < 1)) {
      next.months = "Enter a whole number of months.";
    }
    if (form.amount && Number(form.amount) < 0) next.amount = "Enter 0 or more.";
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    try {
      const response = await grantSubscriptionAPI({
        user_id: user.id,
        course_plan_id: Number(form.planId),
        starts_at: form.startsAt || null,
        months: form.months ? Number(form.months) : null,
        amount: form.amount ? Number(form.amount) : null,
        note: form.note.trim() || null,
      });
      if (!response) return;
      if (response.success) {
        showSuccess(response.message, "Plan granted");
        onGranted?.(response.data);
        onClose();
      } else {
        setErrors({ form: firstError(response, "Couldn't grant the plan.") });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => !saving && onClose()}
      icon="bi bi-gift"
      title="Grant a plan"
      subtitle="For a payment made outside the website. The student gets access straight away."
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={saving}>
            {saving && <span className="btn-spinner"></span>} Grant plan
          </button>
        </>
      }
    >
      {errors.form && (
        <div className="alert alert-danger">
          <i className="bi bi-exclamation-circle"></i>
          <div className="alert-body">{errors.form}</div>
        </div>
      )}

      <div className="form-group">
        <label htmlFor="grant-user">Student</label>
        {user ? (
          <div className="grant-user">
            <div className="cell-stack">
              <strong>{user.name}</strong>
              <small>{user.email}</small>
            </div>
            {!fixedUser && (
              <button type="button" className="btn-ghost btn-sm" onClick={() => setUser(null)}>
                Change
              </button>
            )}
          </div>
        ) : (
          <>
            <input
              id="grant-user"
              type="search"
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Search by name, email or phone…"
              className={errors.user ? "error" : ""}
              autoFocus
            />
            {errors.user && <span className="field-error">{errors.user}</span>}
            {(searching || userResults.length > 0) && (
              <div className="grant-user-results">
                {searching && <span className="text-muted">Searching…</span>}
                {userResults.map((u) => (
                  <button key={u.id} type="button" className="grant-user-option" onClick={() => setUser(u)}>
                    <strong>{u.name}</strong>
                    <small>
                      {u.email}
                      {u.course ? ` · ${u.course}` : ""}
                    </small>
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      <div className="form-group">
        <label htmlFor="grant-plan">Plan</label>
        <select
          id="grant-plan"
          value={form.planId}
          onChange={(e) => set("planId", e.target.value)}
          disabled={loadingCourses}
          className={errors.planId ? "error" : ""}
        >
          <option value="">{loadingCourses ? "Loading plans…" : "Choose a plan"}</option>
          {courses.map((course) =>
            (course.plans || []).length ? (
              <optgroup key={course.id} label={course.course_name}>
                {course.plans.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.plan_name} · {p.plan_duration} months · {formatINR(Number(p.plan_amount) - Number(p.plan_discount || 0))}
                  </option>
                ))}
              </optgroup>
            ) : null
          )}
        </select>
        {errors.planId && <span className="field-error">{errors.planId}</span>}
        {user?.course && plan && plan.course_name !== user.course && (
          <span className="help-text">
            <i className="bi bi-info-circle"></i> This student's course is {user.course}; this plan is for {plan.course_name}.
          </span>
        )}
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="grant-start">Starts on</label>
          <input id="grant-start" type="date" value={form.startsAt} onChange={(e) => set("startsAt", e.target.value)} />
        </div>
        <div className="form-group">
          <label htmlFor="grant-months">
            Months <span className="optional">Optional</span>
          </label>
          <input
            id="grant-months"
            type="number"
            min="1"
            step="1"
            value={form.months}
            onChange={(e) => set("months", e.target.value)}
            placeholder={plan ? `${plan.plan_duration} (plan duration)` : "Plan duration"}
            className={errors.months ? "error" : ""}
          />
          {errors.months && <span className="field-error">{errors.months}</span>}
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="grant-amount">
            Amount received (₹) <span className="optional">Optional</span>
          </label>
          <input
            id="grant-amount"
            type="number"
            min="0"
            step="0.01"
            value={form.amount}
            onChange={(e) => set("amount", e.target.value)}
            placeholder="0"
            className={errors.amount ? "error" : ""}
          />
          {errors.amount ? (
            <span className="field-error">{errors.amount}</span>
          ) : (
            <span className="help-text">Recorded for reference; not counted as online revenue.</span>
          )}
        </div>
        <div className="form-group">
          <label htmlFor="grant-note">
            Note <span className="optional">Optional</span>
          </label>
          <input
            id="grant-note"
            type="text"
            value={form.note}
            onChange={(e) => set("note", e.target.value)}
            placeholder="e.g. Paid in cash at the centre"
            maxLength={1000}
          />
        </div>
      </div>
    </Modal>
  );
}

export default GrantSubscriptionModal;
